import { useState, useEffect } from "react";
import axios from "axios";
import { X, Loader2, ShieldCheck, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Provider Claim modal — used by the "Is this your business?" link on
 * unclaimed CMS-imported listings. Verifies the claimant's NPI against NPPES
 * and shows a live business-name match preview before submission.
 */
export default function ProviderClaimModal({ open, provider, onClose }) {
  const [form, setForm] = useState({ npi: "", contact_name: "", email: "", phone: "", role: "", note: "" });
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(null); // NPPES lookup result
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({ npi: "", contact_name: "", email: "", phone: "", role: "", note: "" });
      setVerified(null);
    }
  }, [open]);

  if (!open || !provider) return null;

  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === "npi") setVerified(null);
  };

  const verify = async () => {
    const npi = form.npi.trim();
    if (!/^\d{10}$/.test(npi)) {
      toast.error("NPI must be exactly 10 digits.");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await axios.get(`${API}/providers/npi-lookup/${npi}`);
      setVerified(data);
      // Prefill phone from NPPES if the field is empty
      if (!form.phone && data.phone) setField("phone", data.phone);
      toast.success(`Verified: ${data.org_name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't verify that NPI.");
      setVerified(null);
    } finally {
      setVerifying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["npi", "contact_name", "email", "phone"];
    for (const k of required) {
      if (!form[k].trim()) {
        toast.error(`Please fill in ${k.replace("_", " ")}.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/providers/${provider.id}/claim`, {
        provider_id: provider.id,
        ...form,
      });
      if (data.auto_approved) {
        toast.success(data.message, { duration: 6000 });
      } else {
        toast.info(data.message, { duration: 6000 });
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Claim couldn't be submitted right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="provider-claim-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-primary">Claim this listing</h2>
              <p className="text-xs font-body text-text-secondary">Verified via CMS NPI Registry</p>
            </div>
          </div>
          <button
            data-testid="claim-modal-close"
            type="button"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary p-1 rounded-full hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
            <p className="text-xs font-body font-semibold uppercase tracking-wide text-text-secondary">Listing</p>
            <p className="font-heading font-bold text-text-primary">{provider.name}</p>
            <p className="text-xs font-body text-text-secondary">{provider.address}</p>
          </div>

          <div>
            <Label htmlFor="claim-npi" className="font-body font-semibold text-text-primary">Your Organization&apos;s NPI *</Label>
            <div className="mt-1 flex gap-2">
              <Input
                id="claim-npi"
                data-testid="claim-npi"
                value={form.npi}
                onChange={(e) => setField("npi", e.target.value)}
                inputMode="numeric"
                maxLength={10}
                className="h-11 font-body font-mono tracking-wider flex-1"
                placeholder="10-digit NPI"
              />
              <Button
                type="button"
                data-testid="claim-verify-btn"
                onClick={verify}
                disabled={verifying || !/^\d{10}$/.test(form.npi.trim())}
                className="h-11 px-4 rounded-lg bg-primary text-white hover:bg-[#238C82] font-body font-semibold disabled:opacity-50"
              >
                {verifying ? "…" : "Verify"}
              </Button>
            </div>
            <p className="text-xs text-text-secondary mt-1 font-body">
              We match your NPI against the CMS registry to auto-approve real agencies.
            </p>
            {verified && (
              <div
                data-testid="claim-verify-result"
                className="mt-2 rounded-lg border-2 border-primary/40 bg-primary/5 p-3"
              >
                <p className="font-body font-bold text-primary text-sm">{verified.org_name}</p>
                <p className="text-xs font-body text-text-secondary">
                  {verified.address.city}, {verified.address.state} {verified.address.zip} · {verified.taxonomy.desc}
                </p>
                {verified.star_rating && (
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-body text-text-secondary">
                    <Star className="w-3 h-3 text-secondary fill-secondary" />
                    CMS Star Rating: {verified.star_rating.rating.toFixed(1)}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="claim-name" className="font-body font-semibold text-text-primary">Your Name *</Label>
              <Input id="claim-name" data-testid="claim-name" value={form.contact_name}
                onChange={(e) => setField("contact_name", e.target.value)} className="mt-1 h-11 font-body" />
            </div>
            <div>
              <Label htmlFor="claim-role" className="font-body font-semibold text-text-primary">Your Role</Label>
              <Input id="claim-role" data-testid="claim-role" value={form.role}
                onChange={(e) => setField("role", e.target.value)} className="mt-1 h-11 font-body"
                placeholder="Owner, Administrator, etc." />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="claim-email" className="font-body font-semibold text-text-primary">Work Email *</Label>
              <Input id="claim-email" type="email" data-testid="claim-email" value={form.email}
                onChange={(e) => setField("email", e.target.value)} className="mt-1 h-11 font-body" />
            </div>
            <div>
              <Label htmlFor="claim-phone" className="font-body font-semibold text-text-primary">Phone *</Label>
              <Input id="claim-phone" type="tel" data-testid="claim-phone" value={form.phone}
                onChange={(e) => setField("phone", e.target.value)} className="mt-1 h-11 font-body" />
            </div>
          </div>

          <div>
            <Label htmlFor="claim-note" className="font-body font-semibold text-text-primary">Note (optional)</Label>
            <Textarea id="claim-note" data-testid="claim-note" rows={3} value={form.note}
              onChange={(e) => setField("note", e.target.value)} className="mt-1 font-body"
              placeholder="Anything you'd like the Tyvena team to know" />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}
              className="flex-1 h-11 rounded-full border-2 border-primary text-primary hover:bg-primary/10 font-body font-semibold">
              Cancel
            </Button>
            <Button type="submit" data-testid="claim-submit" disabled={submitting}
              className="flex-1 h-11 rounded-full bg-primary text-white hover:bg-[#238C82] font-body font-bold">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "Submit Claim"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
